import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { ProductFotoGrid } from "@/components/ProductFotoGrid";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (v) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(Number(v || 0));

function CoverDia({ data, logoUrl, coverUrl }) {
  const el = data.slide_elementen?.cover || {};
  const c = data.merkkleuren || {};
  const balkAan = el.balk?.aan !== false;
  const balkKleur = el.balk?.kleur || c.primair || "#1A1A1A";
  const mix = (hex, f, dir) => {
    const h = (hex || "#1A1A1A").replace("#", "");
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    const t = (v) => (dir > 0 ? Math.round(v + (255 - v) * f) : Math.round(v * (1 - f)));
    return `rgb(${t(r)}, ${t(g)}, ${t(b)})`;
  };
  const titelKleur = el.titel?.kleur || (balkAan ? "#FFFFFF" : c.primair);
  const subKleur = el.subtitel?.kleur || (balkAan ? "#FFFFFF" : c.secundair);
  return (
    <div className="relative w-full aspect-video bg-white overflow-hidden">
      {coverUrl && el.achtergrond?.aan !== false && (
        <img src={coverUrl} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
      )}
      {balkAan && (
        <div className="absolute left-0 right-0 bottom-0 h-[22%]"
          style={{ background: `linear-gradient(to bottom, ${mix(balkKleur, 0.45, 1)}, ${mix(balkKleur, 0.25, -1)})` }} />
      )}
      {logoUrl && (
        <img src={logoUrl} alt="logo" className="absolute top-[3%] right-[3%] h-[10%] object-contain object-right" />
      )}
      <div className="absolute left-[5%] right-[10%] bottom-[8%]" style={{ fontFamily: "Avenir, ui-sans-serif" }}>
        <div className="font-semibold" style={{ color: titelKleur, fontSize: "clamp(18px, 3.2vw, 40px)", lineHeight: 1.1 }}>
          {data.dias?.[0]?.titel}
        </div>
        <div className="mt-2" style={{ color: subKleur, fontSize: "clamp(11px, 1.4vw, 18px)" }}>
          {data.dias?.[0]?.subtitel}
        </div>
      </div>
    </div>
  );
}

function OverOnsDia({ dia, data, logoUrl }) {
  const el = data.slide_elementen?.over_ons || {};
  const c = data.merkkleuren || {};
  return (
    <div className="relative w-full aspect-video bg-white overflow-hidden" style={{ fontFamily: "Avenir" }}>
      {logoUrl && <img src={logoUrl} alt="logo" className="absolute top-[3%] right-[3%] h-[10%] object-contain object-right" />}
      <div className="absolute left-[5%] right-[5%] top-[16%] font-semibold"
        style={{ color: el.titel?.kleur || c.primair, fontSize: "clamp(18px, 2.6vw, 32px)" }}>
        {dia.titel}
      </div>
      <div className="absolute left-[5%] right-[5%] top-[30%] bottom-[6%] whitespace-pre-wrap"
        style={{ color: el.tekst?.kleur || c.tekst || "#1A1A1A", fontSize: "clamp(11px, 1.15vw, 15px)", lineHeight: 1.5 }}>
        {dia.tekst}
      </div>
    </div>
  );
}

function ProductDia({ dia, data, logoUrl }) {
  const el = data.slide_elementen?.product || {};
  const c = data.merkkleuren || {};
  const L = data.labels || {};
  const fotos = (dia.fotos || []).map((f) => ({ url: `${API_BASE}${f.url}`, alt: dia.naam }));
  const bpText = dia.bestelbaar_per > 0 ? `${L.verpakking}: ${dia.bestelbaar_per} ${L.per_stuks}` : "";
  return (
    <div className="relative w-full aspect-video bg-white overflow-hidden" style={{ fontFamily: "Avenir" }}>
      {logoUrl && <img src={logoUrl} alt="logo" className="absolute top-[3%] right-[3%] h-[10%] object-contain object-right" />}
      <div className="absolute left-[5%] right-[26%] top-[14%] font-semibold"
        style={{ color: el.titel?.kleur || c.primair, fontSize: "clamp(14px, 1.9vw, 24px)", lineHeight: 1.15 }}>
        {dia.naam}
      </div>
      <div className="absolute left-[5%] top-[25%]"
        style={{ color: el.sku?.kleur || c.secundair, fontSize: "clamp(10px, 1vw, 13px)" }}>
        {dia.sku}
      </div>
      {/* Foto-grid: hoofdfoto contain + 2 thumbs cover */}
      <div className="absolute" style={{ left: "5%", top: "33%", width: "38%", height: "60%" }}>
        <ProductFotoGrid fotos={fotos} alt={dia.naam} />
      </div>
      {/* Tekstblok rechts */}
      <div className="absolute overflow-hidden whitespace-pre-wrap"
        style={{
          left: "46%", top: "33%", width: "49%", height: "42%",
          color: el.tekst?.kleur || c.tekst || "#1A1A1A", fontSize: "clamp(10px, 1vw, 14px)", lineHeight: 1.45,
        }}>
        {dia.tekst}
        {dia.claims && <div className="mt-2" style={{ color: c.secundair }}>{L.claims}: {dia.claims}</div>}
        {dia.huidtype && <div style={{ color: c.secundair }}>{L.huidtype}: {dia.huidtype}</div>}
      </div>
      <div className="absolute font-semibold"
        style={{ left: "46%", top: "80%", color: el.prijs?.kleur || c.accent, fontSize: "clamp(13px, 1.6vw, 22px)" }}>
        {L.prijs}: {fmtEur(dia.prijs)}
      </div>
      {bpText && (
        <div className="absolute" style={{ left: "46%", top: "88%", color: el.verpakking?.kleur || c.secundair, fontSize: "clamp(10px, 1vw, 13px)" }}>
          {bpText}
        </div>
      )}
    </div>
  );
}

function BestellijstDia({ dia, data, logoUrl }) {
  const el = data.slide_elementen?.bestellijst || {};
  const c = data.merkkleuren || {};
  const L = data.labels || {};
  return (
    <div className="relative w-full aspect-video bg-white overflow-hidden" style={{ fontFamily: "Avenir" }}>
      {logoUrl && <img src={logoUrl} alt="logo" className="absolute top-[3%] right-[3%] h-[10%] object-contain object-right" />}
      <div className="absolute left-[5%] right-[5%] top-[14%] font-semibold"
        style={{ color: el.titel?.kleur || c.primair, fontSize: "clamp(16px, 2.2vw, 28px)" }}>
        {dia.titel}
      </div>
      <div className="absolute left-[5%] right-[5%] top-[26%] bottom-[16%] overflow-hidden">
        <div className="grid grid-cols-[1.2fr_3fr_1fr_1fr] gap-x-3 border-b pb-1.5" style={{ color: el.tabel?.kleur || c.primair, borderColor: (el.tabel?.kleur || c.primair) + "40" }}>
          <div className="text-xs font-bold">{L.artikel}</div>
          <div className="text-xs font-bold">{L.product}</div>
          <div className="text-xs font-bold">{L.prijs}</div>
          <div className="text-xs font-bold">{L.aantal}</div>
        </div>
        <div className="overflow-y-auto max-h-full">
          {(dia.rijen || []).map((r, i) => (
            <div key={i} className="grid grid-cols-[1.2fr_3fr_1fr_1fr] gap-x-3 py-1 text-[11px] border-b border-border/60 text-foreground/80">
              <div>{r.sku}</div>
              <div className="truncate">{r.naam}</div>
              <div>{fmtEur(r.prijs)}</div>
              <div>{r.bestelbaar_per > 0 ? `${r.bestelbaar_per} ${L.per_stuks}` : ""}</div>
            </div>
          ))}
        </div>
      </div>
      {dia.min_bedrag != null && (
        <div className="absolute left-[5%] right-[5%] bottom-[6%] text-xs"
          style={{ color: el.min_bestel?.kleur || c.accent }}>
          {L.min_bestel}: {fmtEur(dia.min_bedrag)}
        </div>
      )}
    </div>
  );
}

export default function PresentatieVoorbeeld() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [full, setFull] = useState(false);

  const taal = params.get("taal") || "nl";
  const productIds = useMemo(() => (params.get("producten") || "").split(",").filter(Boolean), [params]);

  useEffect(() => {
    if (!productIds.length) { toast.error("Geen producten opgegeven"); setLoading(false); return; }
    setLoading(true);
    api.post(`/organisaties/${id}/presentatie-voorbeeld`, { product_ids: productIds, taal })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Voorbeeld laden mislukt"))
      .finally(() => setLoading(false));
  }, [id, productIds, taal]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, (data?.dias?.length || 1) - 1));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
      if (e.key === "Escape") setFull(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;
  const dia = data.dias?.[idx];
  const logoUrl = data.logo_id ? `${API_BASE}/api/instellingen/presentatie/bestanden/logo/${data.logo_id}` : null;
  const coverUrl = data.slide_elementen?.cover?.achtergrond?.aan !== false && data.dias?.[0]?.cover_id
    ? `${API_BASE}/api/instellingen/presentatie/bestanden/cover/${data.dias[0].cover_id}` : null;

  return (
    <div className={`min-h-screen ${full ? "bg-black" : "bg-secondary"}`} data-testid="presentatie-voorbeeld">
      {!full && (
        <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="voorbeeld-terug">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Terug
          </Button>
          <div className="text-sm text-muted-foreground">
            Voorbeeldpresentatie voor <span className="font-medium text-foreground">{data.org.naam}</span> — {data.dias.length} dia{data.dias.length === 1 ? "" : "'s"}
          </div>
          <Button variant="outline" size="sm" onClick={() => setFull(true)} data-testid="voorbeeld-fullscreen">
            <Maximize2 className="h-4 w-4 mr-1.5" /> Volledig scherm
          </Button>
        </div>
      )}
      <div className={`${full ? "fixed inset-0 flex items-center justify-center p-4" : "max-w-6xl mx-auto p-6"}`}>
        <div className="w-full shadow-2xl rounded-lg overflow-hidden border border-border relative bg-white">
          {dia?.type === "cover" && <CoverDia data={data} logoUrl={logoUrl} coverUrl={coverUrl} />}
          {dia?.type === "over_ons" && <OverOnsDia dia={dia} data={data} logoUrl={logoUrl} />}
          {dia?.type === "product" && <ProductDia dia={dia} data={data} logoUrl={logoUrl} />}
          {dia?.type === "bestellijst" && <BestellijstDia dia={dia} data={data} logoUrl={logoUrl} />}
          {full && (
            <Button variant="ghost" onClick={() => setFull(false)} data-testid="voorbeeld-exit-full"
              className="absolute top-3 right-3 h-8 text-white hover:bg-white/10">
              <Minimize2 className="h-4 w-4 mr-1.5" /> Sluit
            </Button>
          )}
        </div>
      </div>
      <div className={`${full ? "fixed bottom-6 left-1/2 -translate-x-1/2" : "max-w-6xl mx-auto px-6 pb-8"} flex items-center gap-3 ${full ? "" : "justify-center"}`}>
        <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)}
          data-testid="voorbeeld-vorige" className={full ? "bg-white/90" : ""}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Vorige
        </Button>
        <span className={`text-sm tabular-nums px-2 ${full ? "text-white" : "text-muted-foreground"}`} data-testid="voorbeeld-teller">
          Dia {idx + 1} / {data.dias.length}
        </span>
        <Button variant="outline" size="sm" disabled={idx >= data.dias.length - 1} onClick={() => setIdx((i) => i + 1)}
          data-testid="voorbeeld-volgende" className={full ? "bg-white/90" : ""}>
          Volgende <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
